// DbGridMenu/index.tsx
import React, { useMemo, useCallback, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, LayoutChangeEvent } from 'react-native';
import DbIcon from '../DbIcon';
import { createStyles, GRID_MENU_DEFAULTS } from './DbGridMenu.styles';
import type { DbGridMenuProps, GridMenuItem } from './DbGridMenu.types';

export const DbGridMenu: React.FC<DbGridMenuProps> = ({
  data,
  layout = 'grid',
  columns = GRID_MENU_DEFAULTS.columns,
  title,
  titleStyle,
  showTitle = true,
  rightText,
  rightIcon,
  rightIconName,
  rightIconFamily = 'Ionicons',
  onRightPress,
  style,
  headerStyle,
  itemStyle,
  itemTextStyle,
  gap = GRID_MENU_DEFAULTS.gap,
  padding = GRID_MENU_DEFAULTS.padding,
  horizontalPadding = GRID_MENU_DEFAULTS.horizontalPadding,
  showDivider = false,
  backgroundColor,
  itemBackgroundColor,
  itemRadius = GRID_MENU_DEFAULTS.itemRadius,
  onItemPress,
}) => {
  const styles = useMemo(() => createStyles('light'), []);
  
  // 使用 state 存储容器宽度
  const [containerWidth, setContainerWidth] = useState(0);

  // 处理容器布局变化
  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setContainerWidth(width);
  }, []);

  // 将数据分组成多行
  const rows = useMemo(() => {
    const result: GridMenuItem[][] = [];
    for (let i = 0; i < data.length; i += columns) {
      result.push(data.slice(i, i + columns));
    }
    return result;
  }, [data, columns]);

  // 计算每个 Grid 项的宽度
  const itemWidth = useMemo(() => {
    if (layout === 'scroll' || containerWidth === 0) return undefined;
    const totalHorizontalPadding = horizontalPadding * 2;
    const availableWidth = containerWidth - totalHorizontalPadding;
    // 如果有背景色，需要减去项目之间的间距
    if (itemBackgroundColor) {
      const totalGap = gap * (columns - 1);
      return (availableWidth - totalGap) / columns;
    }
    // 无背景色时均分
    return availableWidth / columns;
  }, [layout, columns, horizontalPadding, itemBackgroundColor, gap, containerWidth]);

  // 处理项目点击
  const handleItemPress = useCallback((item: GridMenuItem) => {
    if (item.disabled) return;
    item.onPress?.();
    onItemPress?.(item);
  }, [onItemPress]);

  // 渲染图标
  const renderIcon = useCallback((item: GridMenuItem) => {
    const iconSize = item.iconSize || GRID_MENU_DEFAULTS.iconSize;
    
    if (item.icon) {
      return item.icon;
    }

    if (item.iconName) {
      return (
        <DbIcon
          name={item.iconName}
          family={item.iconFamily || 'Ionicons'}
          size={iconSize}
          color={item.iconColor || '#007AFF'}
        />
      );
    }

    return null;
  }, []);

  // 渲染徽标
  const renderBadge = useCallback((item: GridMenuItem) => {
    if (!item.badge) return null;

    const badgeText = typeof item.badge === 'number' && item.badge > 99 
      ? '99+' 
      : String(item.badge);

    return (
      <View style={[
        styles.badge,
        item.badgeColor && { backgroundColor: item.badgeColor }
      ]}>
        <Text style={styles.badgeText}>{badgeText}</Text>
      </View>
    );
  }, [styles]);

  // 渲染标签
  const renderTag = useCallback((item: GridMenuItem) => {
    if (!item.tag) return null;

    return (
      <View style={[
        styles.tag,
        item.tagColor && { backgroundColor: item.tagColor }
      ]}>
        <Text style={styles.tagText}>{item.tag}</Text>
      </View>
    );
  }, [styles]);

  // 渲染单个菜单项
  const renderItem = useCallback((item: GridMenuItem, index: number, isLastInRow: boolean) => {
    const isGrid = layout === 'grid';
    const itemContainerStyle = isGrid ? styles.gridItem : styles.scrollItem;
    
    return (
      <TouchableOpacity
        key={item.id || index}
        style={[
          itemContainerStyle,
          isGrid && itemWidth ? { width: itemWidth } : undefined,
          itemBackgroundColor && styles.itemWithBackground,
          itemBackgroundColor && { 
            backgroundColor: itemBackgroundColor,
            borderRadius: itemRadius,
          },
          // 添加右边距（除了最后一个）
          isGrid && itemBackgroundColor && !isLastInRow && { marginRight: gap },
          item.disabled && styles.itemDisabled,
          itemStyle,
        ]}
        onPress={() => handleItemPress(item)}
        activeOpacity={0.7}
        disabled={item.disabled}
      >
        <View style={[
          styles.iconContainer,
          !itemBackgroundColor && styles.iconContainerSmall,
        ]}>
          {renderIcon(item)}
          {renderBadge(item)}
          {renderTag(item)}
        </View>
        <Text
          style={[
            styles.itemText,
            item.disabled && styles.itemTextDisabled,
            itemTextStyle,
          ]}
          numberOfLines={1}
        >
          {item.title}
        </Text>
      </TouchableOpacity>
    );
  }, [
    layout,
    itemWidth,
    itemBackgroundColor,
    itemRadius,
    gap,
    itemStyle,
    itemTextStyle,
    styles,
    handleItemPress,
    renderIcon,
    renderBadge,
    renderTag,
  ]);

  // 渲染标题栏
  const renderHeader = useCallback(() => {
    if (!showTitle && !title && !rightText && !rightIcon && !rightIconName) {
      return null;
    }

    return (
      <>
        <View style={[styles.header, headerStyle]}>
          <View style={styles.headerLeft}>
            {title && (
              <Text style={[styles.title, titleStyle]}>{title}</Text>
            )}
          </View>
          {(rightText || rightIcon || rightIconName) && (
            <TouchableOpacity
              style={styles.headerRight}
              onPress={onRightPress}
              activeOpacity={0.7}
            >
              {rightText && (
                <Text style={styles.rightText}>{rightText}</Text>
              )}
              {rightIcon || (rightIconName && (
                <DbIcon
                  name={rightIconName}
                  family={rightIconFamily}
                  size={16}
                  color="#999999"
                />
              ))}
            </TouchableOpacity>
          )}
        </View>
        {showDivider && <View style={styles.divider} />}
      </>
    );
  }, [
    showTitle,
    title,
    titleStyle,
    headerStyle,
    rightText,
    rightIcon,
    rightIconName,
    rightIconFamily,
    onRightPress,
    showDivider,
    styles,
  ]);

  // 渲染一行
  const renderRow = useCallback((rowData: GridMenuItem[], rowIndex: number, isLastRow: boolean) => {
    return (
      <View 
        key={`row-${rowIndex}`}
        style={[
          styles.gridRow,
          // 无背景色时使用 space-between，有背景色时使用 flex-start
          !itemBackgroundColor && { justifyContent: 'space-between' },
          itemBackgroundColor && { justifyContent: 'flex-start' },
          // 添加行间距（除了最后一行）
          !isLastRow && { marginBottom: gap },
        ]}
      >
        {rowData.map((item, index) => 
          renderItem(item, rowIndex * columns + index, index === rowData.length - 1)
        )}
      </View>
    );
  }, [styles, itemBackgroundColor, gap, columns, renderItem]);

  // 渲染内容
  const renderContent = useCallback(() => {
    if (layout === 'scroll') {
      return (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.scrollContainer}
          contentContainerStyle={styles.scrollContent}
        >
          {data.map((item, index) => (
            <View key={item.id || index} style={index < data.length - 1 ? { marginRight: gap } : undefined}>
              {renderItem(item, index, index === data.length - 1)}
            </View>
          ))}
        </ScrollView>
      );
    }

    // 等待容器宽度测量完成
    if (containerWidth === 0) {
      return null;
    }

    return (
      <View style={[
        styles.gridContainer,
        { 
          paddingHorizontal: horizontalPadding, 
          paddingVertical: padding,
        },
      ]}>
        {rows.map((rowData, rowIndex) => 
          renderRow(rowData, rowIndex, rowIndex === rows.length - 1)
        )}
      </View>
    );
  }, [layout, data, rows, gap, padding, horizontalPadding, containerWidth, styles, renderItem, renderRow]);

  return (
    <View 
      style={[
        styles.container,
        backgroundColor && { backgroundColor },
        style,
      ]}
      onLayout={handleLayout}
    >
      {renderHeader()}
      {renderContent()}
    </View>
  );
};

export default DbGridMenu;
export type { DbGridMenuProps, GridMenuItem };
